// Minimal, dependency-free QR Code generator (byte mode), adapted from
// Project Nayuki's MIT-licensed "QR Code generator library" (TypeScript).
// Trimmed to what the `qr` display format needs: encode a UTF-8 string and
// expose the boolean module matrix. No DOM / canvas dependency — the cell
// renderer paints the matrix as inline SVG.
//
// Source: https://www.nayuki.io/page/qr-code-generator-library  (MIT License)

/* eslint-disable no-bitwise */

export type QrEcc = 'L' | 'M' | 'Q' | 'H'

const ECC_ORDINAL: Record<QrEcc, number> = { L: 0, M: 1, Q: 2, H: 3 }
const ECC_FORMAT_BITS: Record<QrEcc, number> = { L: 1, M: 0, Q: 3, H: 2 }

class QrSegment {
  constructor(
    readonly mode: { modeBits: number; numBitsCharCount: [number, number, number] },
    readonly numChars: number,
    readonly bitData: number[],
  ) {}
}

const MODE_BYTE = { modeBits: 0x4, numBitsCharCount: [8, 16, 16] as [number, number, number] }

function toUtf8Bytes(str: string): number[] {
  const out: number[] = []
  // encodeURIComponent yields %XX for every non-ASCII byte → reliable UTF-8.
  const esc = encodeURIComponent(str)
  for (let i = 0; i < esc.length; i++) {
    if (esc.charAt(i) === '%') {
      out.push(parseInt(esc.substring(i + 1, i + 3), 16))
      i += 2
    } else {
      out.push(esc.charCodeAt(i))
    }
  }
  return out
}

function makeBytesSegment(data: number[]): QrSegment {
  const bits: number[] = []
  for (const b of data) {
    for (let i = 7; i >= 0; i--) bits.push((b >>> i) & 1)
  }
  return new QrSegment(MODE_BYTE, data.length, bits)
}

// Reed–Solomon ECC generator polynomial divisor for the given degree.
function rsDivisor(degree: number): number[] {
  const result: number[] = []
  for (let i = 0; i < degree - 1; i++) result.push(0)
  result.push(1)
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = rsMultiply(result[j], root)
      if (j + 1 < result.length) result[j] ^= result[j + 1]
    }
    root = rsMultiply(root, 0x02)
  }
  return result
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result = divisor.map(() => 0)
  for (const b of data) {
    const factor = b ^ (result.shift() as number)
    result.push(0)
    divisor.forEach((coef, i) => { result[i] ^= rsMultiply(coef, factor) })
  }
  return result
}

function rsMultiply(x: number, y: number): number {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z & 0xff
}

const ECC_CODEWORDS_PER_BLOCK = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
]
const NUM_ERROR_CORRECTION_BLOCKS = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
]

export interface QrMatrix {
  size: number
  modules: boolean[][] // [y][x]
}

function getNumDataCodewords(ver: number, ecc: QrEcc): number {
  const eccIdx = ECC_ORDINAL[ecc]
  const totalCodewords = Math.floor(getNumRawDataModules(ver) / 8)
  const eccPerBlock = ECC_CODEWORDS_PER_BLOCK[eccIdx][ver]
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[eccIdx][ver]
  return totalCodewords - eccPerBlock * numBlocks
}

function getNumRawDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2
    result -= (25 * numAlign - 10) * numAlign - 55
    if (ver >= 7) result -= 36
  }
  return result
}

export function encodeQr(text: string, ecc: QrEcc = 'M'): QrMatrix {
  const data = toUtf8Bytes(text)
  const seg = makeBytesSegment(data)

  // Choose the smallest version (1..40) that fits.
  let version = 1
  for (; version <= 40; version++) {
    const dataCapacityBits = getNumDataCodewords(version, ecc) * 8
    const ccBits = seg.mode.numBitsCharCount[version <= 9 ? 0 : version <= 26 ? 1 : 2]
    const usedBits = 4 + ccBits + seg.bitData.length
    if (usedBits <= dataCapacityBits) break
  }
  if (version > 40) {
    // Too long for QR — fall back to the largest version (renderer will still
    // draw something, but callers should keep `qr` inputs short).
    version = 40
  }

  // Build the bit buffer.
  const bb: number[] = []
  const appendBits = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1)
  }
  const ccBits = seg.mode.numBitsCharCount[version <= 9 ? 0 : version <= 26 ? 1 : 2]
  appendBits(seg.mode.modeBits, 4)
  appendBits(seg.numChars, ccBits)
  for (const b of seg.bitData) bb.push(b)

  const dataCapacityBits = getNumDataCodewords(version, ecc) * 8
  appendBits(0, Math.min(4, dataCapacityBits - bb.length))
  appendBits(0, (8 - (bb.length % 8)) % 8)
  for (let pad = 0xec; bb.length < dataCapacityBits; pad ^= 0xec ^ 0x11) appendBits(pad, 8)

  // Pack bits into data codewords.
  const dataCodewords: number[] = []
  for (let i = 0; i < bb.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bb[i + j]
    dataCodewords.push(byte)
  }

  const allCodewords = addEccAndInterleave(dataCodewords, version, ecc)
  return drawMatrix(allCodewords, version, ecc)
}

function addEccAndInterleave(data: number[], ver: number, ecc: QrEcc): number[] {
  const eccIdx = ECC_ORDINAL[ecc]
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[eccIdx][ver]
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[eccIdx][ver]
  const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8)
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks)
  const shortBlockLen = Math.floor(rawCodewords / numBlocks)

  const blocks: number[][] = []
  const rsDiv = rsDivisor(blockEccLen)
  let k = 0
  for (let i = 0; i < numBlocks; i++) {
    const datLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1)
    const dat = data.slice(k, k + datLen)
    k += datLen
    const eccBytes = rsRemainder(dat.slice(), rsDiv)
    if (i < numShortBlocks) dat.push(0)
    blocks.push(dat.concat(eccBytes))
  }

  const result: number[] = []
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
        result.push(blocks[j][i])
      }
    }
  }
  return result
}

function drawMatrix(codewords: number[], ver: number, ecc: QrEcc): QrMatrix {
  const size = ver * 4 + 17
  const modules: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const isFunction: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))

  const setFunc = (x: number, y: number, v: boolean) => {
    modules[y][x] = v
    isFunction[y][x] = true
  }

  // Timing patterns.
  for (let i = 0; i < size; i++) {
    setFunc(6, i, i % 2 === 0)
    setFunc(i, 6, i % 2 === 0)
  }

  // Finder patterns (3 corners) + separators.
  const drawFinder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy))
        const xx = cx + dx
        const yy = cy + dy
        if (xx >= 0 && xx < size && yy >= 0 && yy < size) {
          setFunc(xx, yy, dist !== 2 && dist !== 4)
        }
      }
    }
  }
  drawFinder(3, 3)
  drawFinder(size - 4, 3)
  drawFinder(3, size - 4)

  // Alignment patterns.
  const alignPositions = getAlignmentPositions(ver)
  const numAlign = alignPositions.length
  for (let i = 0; i < numAlign; i++) {
    for (let j = 0; j < numAlign; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === numAlign - 1) || (i === numAlign - 1 && j === 0)) continue
      drawAlignment(setFunc, alignPositions[i], alignPositions[j])
    }
  }

  // Reserve format + version info regions as functional (filled later).
  drawFormatBits(setFunc, ecc, 0, size, true)

  // Draw the data + ECC codewords zig-zag.
  let i = 0 // bit index
  for (let right = size - 1; right >= 1; right -= 2) {
    const col = right === 6 ? 5 : right
    for (let vert = 0; vert < size; vert++) {
      for (let k = 0; k < 2; k++) {
        const x = col - k
        const upward = ((col + 1) & 2) === 0
        const y = upward ? size - 1 - vert : vert
        if (!isFunction[y][x] && i < codewords.length * 8) {
          modules[y][x] = ((codewords[i >>> 3] >>> (7 - (i & 7))) & 1) !== 0
          i++
        }
      }
    }
  }

  // Try all 8 masks, pick the lowest penalty.
  let bestMask = 0
  let minPenalty = Infinity
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, mask)
    drawFormatBits(setFunc, ecc, mask, size, false)
    const penalty = penaltyScore(modules, size)
    if (penalty < minPenalty) {
      minPenalty = penalty
      bestMask = mask
    }
    applyMask(modules, isFunction, mask) // undo (XOR is its own inverse)
  }
  applyMask(modules, isFunction, bestMask)
  drawFormatBits(setFunc, ecc, bestMask, size, false)

  return { size, modules }
}

function getAlignmentPositions(ver: number): number[] {
  if (ver === 1) return []
  const numAlign = Math.floor(ver / 7) + 2
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2
  const result = [6]
  for (let pos = ver * 4 + 10; result.length < numAlign; pos -= step) result.splice(1, 0, pos)
  return result
}

function drawAlignment(setFunc: (x: number, y: number, v: boolean) => void, cx: number, cy: number) {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      setFunc(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
    }
  }
}

function drawFormatBits(
  setFunc: (x: number, y: number, v: boolean) => void,
  ecc: QrEcc, mask: number, size: number, reserveOnly: boolean,
) {
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask
  let rem = data
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
  const bits = ((data << 10) | rem) ^ 0x5412

  const bitAt = (i: number) => reserveOnly ? false : ((bits >>> i) & 1) !== 0

  for (let i = 0; i <= 5; i++) setFunc(8, i, bitAt(i))
  setFunc(8, 7, bitAt(6))
  setFunc(8, 8, bitAt(7))
  setFunc(7, 8, bitAt(8))
  for (let i = 9; i < 15; i++) setFunc(14 - i, 8, bitAt(i))

  for (let i = 0; i < 8; i++) setFunc(size - 1 - i, 8, bitAt(i))
  for (let i = 8; i < 15; i++) setFunc(8, size - 15 + i, bitAt(i))
  setFunc(8, size - 8, true) // dark module
}

function applyMask(modules: boolean[][], isFunction: boolean[][], mask: number) {
  const size = modules.length
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (isFunction[y][x]) continue
      let invert = false
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break
        case 1: invert = y % 2 === 0; break
        case 2: invert = x % 3 === 0; break
        case 3: invert = (x + y) % 3 === 0; break
        case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break
        case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break
      }
      if (invert) modules[y][x] = !modules[y][x]
    }
  }
}

function penaltyScore(modules: boolean[][], size: number): number {
  let result = 0
  // Adjacent same-color runs (rows + columns).
  for (let y = 0; y < size; y++) {
    let runColor = false, runLen = 0
    for (let x = 0; x < size; x++) {
      if (modules[y][x] === runColor) { runLen++; if (runLen === 5) result += 3; else if (runLen > 5) result++ }
      else { runColor = modules[y][x]; runLen = 1 }
    }
  }
  for (let x = 0; x < size; x++) {
    let runColor = false, runLen = 0
    for (let y = 0; y < size; y++) {
      if (modules[y][x] === runColor) { runLen++; if (runLen === 5) result += 3; else if (runLen > 5) result++ }
      else { runColor = modules[y][x]; runLen = 1 }
    }
  }
  // 2x2 blocks.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = modules[y][x]
      if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) result += 3
    }
  }
  // Dark proportion.
  let dark = 0
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (modules[y][x]) dark++
  const total = size * size
  const k = Math.floor((Math.abs(dark * 20 - total * 10) + total - 1) / total) - 1
  result += k * 10
  return result
}
